import { useCallback, useContext, useEffect, useMemo } from "react";
import IFinalizePOShipmentDTO from "@models/DTO/IFinalizePOShipmentDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import IBreadcrumbs from "@models/IBreadcrumbs";
import IOwTableCols from "@models/IOwTableCols";
import IPO_Shipment_Catalog_Item_MapUI from "@models/IPO_Shipment_Catalog_Item_MapUI";
import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import OwButton from "@components/shared/OwButton/OwButton";
import OwInputWrapper from "@components/shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTable from "@components/shared/OwTable/OwTable";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { usePurchaseOrderShipmentData } from "@hooks/purchase_orders/usePurchaseOrderShipmentData";
import { usePurchaseOrderShipmentSocket } from "@hooks/purchase_orders/usePurchaseOrderShipmentSocket";
import { usePurchaseOrderShipmentQuantity } from "@hooks/purchase_orders/usePurchaseOrderShipmentQuantity";
import UserContext from "@contexts/UserContext";
import { useMatch } from "react-router-dom";
import SupplierContext from "@contexts/SupplierContext";
import { SocketContext } from "@contexts/SocketContext";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";

export const PurchaseOrderShipmentDetailFn = () => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const match = useMatch("purchase_orders/process_shipment/:guid");

    const crumbs = useMemo<IBreadcrumbs[]>(() => {
        return [
            {
                title: "dashboard",
                url: "/dashboard",
            },
            {
                title: "purchase_orders",
                url: "/purchase_orders",
            },
            {
                title: "shipment_detail",
                url: "",
            },
        ];
    }, []);

    const {
        poShipment,
        setPoShipment,
        poShipmentItems,
        setPoShipmentItems,
        dataFetch,
        setDataFetch,
        initialData,
        setInitialData,
        isFormDirty,
        setIsFormDirty,
        getPOShipment,
        warehouses
    } = usePurchaseOrderShipmentData({
        poShipmentGuid: match?.params.guid || ""
    });

    usePurchaseOrderShipmentSocket({
        setPoShipment,
        setPoShipmentItems,
        setDataFetch,
        setInitialData,
        setIsFormDirty
    });

    const {
        getTotalOrdered,
        copyQtyToIncoming,
        handleIncomingChange,
        handleWarehouseChange,
        savePOShipmentItem
    } = usePurchaseOrderShipmentQuantity({
        poShipment,
        setPoShipmentItems,
        setIsFormDirty
    });

    const finalizePOShipment = useCallback(() => {
        const payload: IFinalizePOShipmentDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            poShipmentGuid: match?.params.guid || "",
        };

        socket.emit(EndpointsEnum.PurchaseOrderFinalizePOShipment, payload);
    }, [userModel.guid, userModel.token, supplierModel.guid, match?.params.guid, socket]);

    // Initial data fetch
    useEffect(() => {
        getPOShipment();
    }, [getPOShipment]);

    // Table rendering functions
    const renderActionsFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        return <div></div>;
    };

    const renderItemFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        return <div>{row.Catalog_Item?.name}</div>;
    };

    const renderQtyFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        const totalOrdered = getTotalOrdered(row);
        const hideCopy = poShipment?.is_processed;

        return (
            <div>
                {totalOrdered}{" "}
                {!hideCopy && (
                    <span className={"text-primary"}>
                        <OwLabel
                            title={"copy"}
                            noBottomMargin={true}
                            rightIconClass={"fas fa-chevron-double-right"}
                            fn={() => copyQtyToIncoming(row)}
                        />
                    </span>
                )}
            </div>
        );
    };

    const renderIncomingQtyFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        if (!poShipment?.is_processed) {
            return (
                <div className={"w-10r d-flex"}>
                    <div className={"me-2"}>
                        <OwInputWrapper
                            name={"qty_received"}
                            fn={(e: any) => handleIncomingChange(e, row)}
                            value={row.qty_received}
                        />
                    </div>
                    <div>
                        <OwButton
                            title={"save"}
                            disabled={!row.isDirty}
                            fn={() => savePOShipmentItem(row)}
                        />
                    </div>
                </div>
            );
        }
        return <div>{row.qty_received}</div>;
    };

    const renderWarehouseFn = (row: IPO_Shipment_Catalog_Item_MapUI) => {
        if (!poShipment?.is_processed) {    
            return (<div className="d-flex">
                <OwSelectOption
                    name={"warehouse_id"}
                    options={warehouses}
                    valueProp="id"
                    titleProp="name"
                    fn={(e: any) => handleWarehouseChange(e, row)}
                    selectedOption={row}
                    selectedOptionProp="warehouse_id"
                />
            </div>)
        } else {
            return <div>{row.Warehouse?.name}</div>;
        }
    };

    const columns: IOwTableCols[] = [
        {
            isActionsCol: true,
            title: "",
            valueProp: "",
            renderFn: renderActionsFn,
            renderFnEmitsRow: true,
        },
        {
            title: "item",
            valueProp: "",
            renderFn: renderItemFn,
            renderFnEmitsRow: true,
        },
        {
            title: "qty_remaining",
            valueProp: "",
            renderFn: renderQtyFn,
            renderFnEmitsRow: true,
        },
        
    ];

    if (supplierModel.has_warehouses) {
        columns.push({
            title: "warehouse",
            valueProp: "",
            renderFn: renderWarehouseFn,
            renderFnEmitsRow: true,
        });
    }

    columns.push({
        title: "incoming_qty",
        valueProp: "",
        renderFn: renderIncomingQtyFn,
        renderFnEmitsRow: true,
    });

    return (
        <div className="PurchaseOrderShipmentDetailFn">
            <Breadcrumbs crumbs={crumbs} />

            <div className="row mt-2">
                <div className={"col-12 d-flex align-items-center"}>
                    <OwTitle title={"shipment_detail"} leftIconName={"fad fa-truck-ramp"} />
                    {!poShipment?.is_processed ? (
                        <div className={"ms-auto d-flex flex-column justify-content-end"}>
                            <div className={"text-end"}>
                                <OwButton title={"finalize"} fn={finalizePOShipment} disabled={isFormDirty} />
                            </div>
                            <div>
                                {isFormDirty ? (
                                    <OwLabel title={"save_all_changes_to_finalize"} className={"mt-2 text-warning"} />
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className={"mt-2 mb-4"}>
                <OwLabel title={"purchase_order"} postText={":"} className={"me-2"} />
                {poShipment?.PO_Shipment_Purchase_Order_Maps?.map((mapItem) => {
                    return <span key={mapItem.guid}>{mapItem?.Purchase_Order?.name}</span>;
                })}
            </div>

            <div>
                <OwTable
                    columns={columns}
                    dataSet={poShipmentItems || []}
                    userModel={userModel}
                    supplierModel={supplierModel}
                    dataFetch={dataFetch}
                    initialData={initialData}
                />
            </div>
        </div>
    );
};
